import React, { useEffect, useMemo, useState } from "react";
import "./CartPage.css";
import RecentlyViewed from "../layouts/components/RecentlyViewed";
import { cartAPI, addressAPI } from "../utils/zone-api";
import AddressForm from "../components/AddressForm";
import toast from "react-hot-toast";

const API_BASE = "https://api.mariamlyluxe.com/public";
const STORAGE_BASE = `${API_BASE}/storage/`;
const PLACEHOLDER_IMG = "/img/product-placeholder.png"; // بدّله إذا بدك

// ==== helpers ====
function getToken() {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("auth_token") ||
    ""
  );
}
function toNum(v) {
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}
function formatPrice(n) {
  const val = toNum(n);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(val);
}
function getImageUrl(prod) {
  if (!prod) return PLACEHOLDER_IMG;
  const img = prod.image || prod.image_url || prod.thumbnail;
  if (!img) return PLACEHOLDER_IMG;
  if (typeof img === "string" && !img.startsWith("http")) {
    return STORAGE_BASE + img.replace(/^\/+/, "");
  }
  return img;
}

export default function CartPage() {
  const token = useMemo(getToken, []);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ subtotal: 0, shipping: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // productId أثناء العملية
  
  // Address and shipping state
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const headers = useMemo(() => {
    const h = { Accept: "application/json", "Content-Type": "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  // ===== API calls =====
  async function fetchCart(addressId = null) {
    setLoading(true);
    try {
      const data = await cartAPI.getCart(token, addressId);
      
      if (data.success) {
        const list = data.items || [];
        setItems(list);

        const s = data.summary || {};
        setSummary({
          subtotal: toNum(s.subtotal),
          shipping: toNum(s.shipping),
          total: toNum(s.total),
        });
      } else {
        setItems([]);
        setSummary({ subtotal: 0, shipping: 0, total: 0 });
      }
    } catch (e) {
      console.warn("Cart fetch error:", e);
      setItems([]);
      setSummary({ subtotal: 0, shipping: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  }

  async function fetchAddresses() {
    if (!token) return;
    
    try {
      const data = await addressAPI.getAddresses(token);
      if (data.success) {
        setAddresses(data.addresses || []);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  }

  useEffect(() => {
    fetchCart();
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch cart with shipping when address is selected
  useEffect(() => {
    if (selectedAddressId) {
      fetchCart(selectedAddressId);
    }
  }, [selectedAddressId]);

  // == value helpers
  function productIdOf(it) {
    return it?.product_id ?? it?.product?.id;
  }
  function unitPriceOf(it) {
    const p = it?.product;
    const price = p?.selling_price ?? p?.price ?? p?.regular_price ?? 0;
    return toNum(price);
  }
  function currentQty(it) {
    return (
      toNum(it?.quantity) ||
      toNum(it?.qty) ||
      toNum(it?.pivot?.quantity) ||
      1
    );
  }
  function lineTotal(it) {
    return unitPriceOf(it) * currentQty(it);
  }

  // ===== Address handling =====
  async function handleAddressSubmit(addressData) {
    try {
      if (editingAddress) {
        await addressAPI.updateAddress(editingAddress.id, addressData, token);
        toast.success("Address updated successfully");
      } else {
        await addressAPI.createAddress(addressData, token);
        toast.success("Address created successfully");
      }
      
      await fetchAddresses();
      setShowAddressForm(false);
      setEditingAddress(null);
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Failed to save address");
    }
  }

  function handleEditAddress(address) {
    setEditingAddress(address);
    setShowAddressForm(true);
  }

  async function handleDeleteAddress(addressId) {
    if (!window.confirm("Are you sure you want to delete this address?")) return;
    
    try {
      await addressAPI.deleteAddress(addressId, token);
      toast.success("Address deleted successfully");
      await fetchAddresses();
      
      // If deleted address was selected, clear selection
      if (selectedAddressId === addressId) {
        setSelectedAddressId(null);
        fetchCart(); // Fetch cart without shipping
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      toast.error("Failed to delete address");
    }
  }

  // ===== Quantity change (+/-) =====
  async function changeQtyBy(it, delta) {
    const prodId = productIdOf(it);
    if (!prodId) return;

    const before = currentQty(it);
    const after = Math.max(0, before + delta);

    // لو 0 -> احذف
    if (after === 0) {
      return removeByProductId(prodId, it.id);
    }

    // optimistic UI
    setItems((prev) =>
      prev.map((x) =>
        productIdOf(x) === prodId ? { ...x, quantity: String(after) } : x
      )
    );

    // quick recompute subtotal locally
    const newSubtotal = items.reduce((acc, x) => {
      const q =
        productIdOf(x) === prodId ? after : currentQty(x);
      return acc + unitPriceOf(x) * q;
      // eslint-disable-next-line
    }, 0);
    setSummary((s) => ({ ...s, subtotal: newSubtotal, total: newSubtotal + toNum(s.shipping) }));

    try {
      setBusyId(prodId);
      // PUT /api/cart/{productId}  body: {quantity}
      const r = await fetch(`${API_BASE}/api/cart/${prodId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ quantity: after }),
      });
      if (!r.ok) throw new Error("PUT /cart/{id} failed");
      await fetchCart(); // sync من السيرفر
    } catch (e) {
      console.warn("changeQty error:", e);
      // rollback
      setItems((prev) =>
        prev.map((x) =>
          productIdOf(x) === prodId ? { ...x, quantity: String(before) } : x
        )
      );
      await fetchCart();
    } finally {
      setBusyId(null);
    }
  }

  // ===== Remove item =====
  async function removeByProductId(prodId, fallbackCartItemId) {
    // optimistic remove
    const prev = items;
    setItems((p) => p.filter((x) => productIdOf(x) !== prodId));

    try {
      setBusyId(prodId);
      // DELETE /api/cart/{productId}
      const r = await fetch(`${API_BASE}/api/cart/${prodId}`, {
        method: "DELETE",
        headers,
      });
      if (!r.ok) throw new Error("DELETE /cart/{id} failed");
      await fetchCart();
    } catch (e) {
      console.warn("remove error:", e);
      setItems(prev); // rollback
      await fetchCart();
    } finally {
      setBusyId(null);
    }
  }

  // ===== UI =====
  if (loading) {
    return (
      <section className="cart-container">
        <h1 className="cart-title">My Cart</h1>
        <div className="cart-grid">
          <div className="cart-skeleton-list">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="cart-skeleton-item">
                <div className="s-img" />
                <div className="s-lines">
                  <div className="s-line w1" />
                  <div className="s-line w2" />
                  <div className="s-line w3" />
                </div>
              </div>
            ))}
          </div>
          <div className="cart-skeleton-summary" />
        </div>
      </section>
    );
  }

  const isEmpty = items.length === 0;

  return (
    <section className="cart-container">
      <h1 className="cart-title">My Cart</h1>

      {isEmpty ? (
        <div className="cart-empty">
          <div className="cart-empty-icon" aria-hidden>🛒</div>
          <h2>Your cart is empty</h2>
          <p>Start exploring our latest offers and add your favorites to cart.</p>
          <a href="/shop">Go to Shop</a>
        </div>
      ) : (
        <div className="cart-grid">
          {/* Items */}
          <div className="cart-items">
            {items.map((it) => {
              const prod = it.product || {};
              const prodId = productIdOf(it);
              const img = getImageUrl(prod);
              const qty = currentQty(it);
              const price = unitPriceOf(it);
              const regular = toNum(prod.regular_price);
              const hasDiscount = regular && regular > price;
              const total = lineTotal(it);

              return (
                <div key={prodId ?? it.id} className="cart-item">
                  <a
                    href={`/product/${prod.id || it.product_id}`}
                    className="cart-item-image"
                    title={prod.name || "Product"}
                  >
                    <img
                      src={img}
                      alt={prod.name || "Product"}
                      onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
                    />
                  </a>

                  <div className="cart-item-details">
                    <div className="cart-item-head">
                      <a
                        href={`/product/${prod.id || it.product_id}`}
                        className="cart-item-name"
                        title={prod.name}
                      >
                        {prod.name || "Product"}
                      </a>
                      <button
                        className="cart-remove"
                        disabled={busyId === prodId}
                        onClick={() => removeByProductId(prodId, it.id)}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="cart-item-price-row">
                      <span className="cart-item-price">{formatPrice(price)}</span>
                      {hasDiscount && (
                        <span className="cart-item-oldprice">
                          {formatPrice(regular)}
                        </span>
                      )}
                      {toNum(prod.discount) > 0 && (
                        <span className="cart-item-discount">
                          -{toNum(prod.discount)}%
                        </span>
                      )}
                    </div>

                    <div className="cart-item-actions">
                      <div className="cart-qty">
                        <button
                          disabled={busyId === prodId || qty <= 0}
                          onClick={() => changeQtyBy(it, -1)}
                          aria-label="Decrease"
                        >
                          −
                        </button>
                        <span>{qty}</span>
                        <button
                          disabled={busyId === prodId}
                          onClick={() => changeQtyBy(it, +1)}
                          aria-label="Increase"
                        >
                          +
                        </button>
                      </div>

                      <div className="cart-line-total">
                        <div>Item total</div>
                        <strong>{formatPrice(total)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          

          {/* Summary */}
          <aside className="cart-summary">
            <h2>Order Summary</h2>
            <div className="cart-summary-row">
              <span>Subtotal</span>
              <span className="v">{formatPrice(summary.subtotal)}</span>
            </div>
            <div className="cart-summary-row">
              <span>Shipping</span>
              <span className="v">
                {summary.shipping ? formatPrice(summary.shipping) : "Calculated at checkout"}
              </span>
            </div>
            <div className="cart-summary-row total">
              <span>Total</span>
              <span className="v">
                {formatPrice(summary.total || summary.subtotal)}
              </span>
            </div>

            <button
              className="cart-checkout"
              onClick={() => (window.location.href = "/checkout")}
            >
              Proceed to Checkout
            </button>
            <div className="cart-summary-note">
              Prices in USD. Taxes & shipping calculated at checkout.
            </div>
        </aside>
      </div>
    )}

    {/* Address Form Modal */}
    {showAddressForm && (
      <div className="modal-overlay" onClick={() => setShowAddressForm(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <AddressForm 
            onSubmit={handleAddressSubmit}
            initialData={editingAddress}
            onCancel={() => {
              setShowAddressForm(false);
              setEditingAddress(null);
            }}
          />
        </div>
      </div>
    )}
    
    {/* Recently Viewed Products */}
    <RecentlyViewed />
  </section>
);
}
